import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { CULTURES, getSemisPourCulture, RCP_OPTIONS, RCP_DEFAUT, COULEURS_PHENO_ECART, STADES_RECO } from '../../shared/constants';
import { libelleHorizon } from '../../shared/utils/horizon';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import CarteRisquePRA from '../../shared/charts/CarteRisquePRA';
import LegendeCarte from '../../shared/charts/LegendeCarte';

// PRA/RA/Département seulement pour ces 3 pages "cartes mock" (pas de
// Région), à la demande d'Aliénor.
const ECHELLES = [
  { value: 'pra', label: 'Petite région agricole' },
  { value: 'ra', label: 'Région agricole' },
  { value: 'departement', label: 'Département' },
];

/**
 * Comparer / Phénologie : 2 cartes (2035/2050), tons bleus, pour la culture
 * choisie — avancement de la maturité par rapport à aujourd'hui.
 * ⚠️ Nouvelle carte, mock (pas de route confirmée) : même principe que la
 * carte d'Impacts globaux (contours réels, valeurs simulées).
 */
export default function ComparerPhenologie() {
  const { idExploit } = useOAD();
  const [culture, setCulture] = React.useState(CULTURES[0].id);
  const semisDisponibles = getSemisPourCulture(culture);
  const [semis, setSemis] = React.useState(semisDisponibles[0]?.value);
  const [rcp, setRcp] = React.useState(RCP_DEFAUT);
  const [echelle, setEchelle] = React.useState(ECHELLES[0].value);
  const [stade, setStade] = React.useState(STADES_RECO[0].id);

  React.useEffect(() => {
    if (!semisDisponibles.some((s) => s.value === semis)) {
      setSemis(semisDisponibles[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culture]);

  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit, idProduction: culture, semis, rcp, echelle, stade }),
    [idExploit, culture, semis, rcp, echelle, stade]
  );

  const { data, loading, error } = useApiData(api.getCartesPhenologie, parametres);

  // Les clés d'horizon changent en TRACC (niveaux de réchauffement, pas des
  // années) : on les lit depuis la réponse plutôt que de les figer ici.
  // Object.keys() ne respecte pas l'ordre d'insertion pour les clés qui
  // ressemblent à des entiers ("2" passe toujours avant "reference", quel
  // que soit l'ordre réel) : on fige l'ordre explicitement plutôt que de
  // faire confiance à Object.keys.
  const ORDRE_HORIZONS = { classique: [2035, 2050], tracc: ['reference', '2', '2.7'] };
  const horizons = data?.cartes
    ? (rcp === 'tracc' ? ORDRE_HORIZONS.tracc : ORDRE_HORIZONS.classique).filter((h) => data.cartes[h])
    : [];

  // Légende unique pour les 2 cartes : bornes calculées sur l'ensemble des
  // features des 2 horizons, pas carte par carte.
  const { min, max } = React.useMemo(() => {
    if (!data?.cartes) return { min: 0, max: 0 };
    const valeurs = horizons.flatMap(
      (h) => data.cartes[h]?.data?.features?.map((f) => Number(f.properties.avancementJours)) ?? []
    );
    return valeurs.length ? { min: Math.min(...valeurs), max: Math.max(...valeurs) } : { min: 0, max: 0 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="comparer-comprendre"
        niveau2="comparer"
        niveau3="pheno"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">Culture</span>
          <select value={culture} onChange={(e) => setCulture(Number(e.target.value))}>
            {CULTURES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Date de semis</span>
          <select value={semis ?? ''} onChange={(e) => setSemis(e.target.value)}>
            {semisDisponibles.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Stade</span>
          <select value={stade} onChange={(e) => setStade(e.target.value)}>
            {STADES_RECO.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Géographie</span>
          <select value={echelle} onChange={(e) => setEchelle(e.target.value)}>
            {ECHELLES.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </label>

        <fieldset className="sidebar-champ sidebar-groupe">
          <legend className="sidebar-label">Scénarios climatiques</legend>
          {RCP_OPTIONS.map((option) => (
            <label key={option.value} className="sidebar-radio">
              <input
                type="radio"
                name="scenario-clim-comparer-pheno"
                value={option.value}
                checked={rcp === option.value}
                onChange={(e) => setRcp(e.target.value)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Avancement de la phénologie</h2>
          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}

          {data?.cartes && (
            <>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {horizons.map((horizon) => (
                  <div key={horizon} style={{ flex: '1 1 320px', minWidth: 280 }}>
                    <h3 className="ig-camembert-titre">{libelleHorizon(rcp, horizon)}</h3>
                    <LimiteErreur cle={`pheno-carte-${horizon}`}>
                      <CarteRisquePRA
                        data={data.cartes[horizon].data}
                        pointExploitation={data.pointExploitation}
                        champ="avancementJours"
                        continu={{ type: 'diverge', ...COULEURS_PHENO_ECART, min, max }}
                        unite="j"
                        afficherLegende={false}
                      />
                    </LimiteErreur>
                  </div>
                ))}
              </div>
              <LegendeCarte continu={{ type: 'diverge', ...COULEURS_PHENO_ECART, min, max }} min={min} max={max} unite="j" />
            </>
          )}
        </section>
      </main>
    </div>
  );
}
